import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Download } from '@factorialco/f0-react/icons/app'
import { Features } from 'generated/featureDefinitions'
import toQueryParams from 'jquery-param'

import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import asyncAction from 'modules/core/lib/asyncAction'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SAVE_ACTION } from 'modules/reports/components/SaveReportModal'
import useReport from 'modules/reports/hooks/useReport'
import ReportStruct from 'modules/reports/lib/ReportStruct'

interface Props {
  reportId?: number
  export?: boolean
  canUseReports: boolean
  saveOnPath: string
}

const NOT_FOUND_REPORT = -1

const EditorHeader = ({ reportId, export: exportReport, canUseReports, saveOnPath }: Props) => {
  const navigate = useNavigate()
  const { featureAvailability } = useFeatureAvailability(Features.analytics_advanced_reports)
  const isAuthorized = featureAvailability.available && canUseReports
  const [isDownloading, setIsLoading] = useState(false)

  const { report } = useReport({
    id: reportId || NOT_FOUND_REPORT,
    loading: false,
  })

  const onDownload = async () => {
    if (!report) {
      return
    }

    setIsLoading(true)
    const reportStruct = ReportStruct.buildFromReport(report)

    app.setFlash({
      type: 'correct',
      message: `${i18n.t('custom_reports.download_loading')}...`,
    })

    // FIXME: Apply filters?
    const { success, value } = await asyncAction(() => reportStruct.order([]), {
      successFlash: false,
    })

    if (success && value && value.token) {
      const query = toQueryParams({
        token: value.token,
        id: report.id,
      })

      const url = buildUrl(`/reports/reports/download?${query}`)
      window.location.href = url

      setIsLoading(false)
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_requesting'),
      })

      setIsLoading(false)
    }
  }

  const title =
    report?.isNew || report == undefined
      ? i18n.t('custom_reports.editor.new')
      : i18n.t('custom_reports.editor.edit_report')

  return (
    <ResourceHeader
      title={title}
      description={i18n.t('custom_reports.editor.fill')}
      primaryAction={{
        label: i18n.t('custom_reports.editor.save'),
        isVisible: isAuthorized,
        onClick: () => {
          navigate({
            pathname: saveOnPath,
            search: `?action=${SAVE_ACTION}`,
          })
        },
      }}
      secondaryActions={[
        {
          label: i18n.t('custom_reports.editor.export'),
          icon: Download,
          disabled: isDownloading,
          isVisible: isAuthorized && !!exportReport && report?.sqlQuery == null,
          onClick: () => {
            onDownload()
          },
        },
      ]}
    />
  )
}

export default EditorHeader
